import { Alert, Button, Card, Col, Form, Row } from "react-bootstrap";
import Field from "../Field/Field";
import {
  fetchAndSetSavedSearches,
  handleBusinessSearchFormChange,
  handleBusinessSearchSubmit,
  handleDeleteSaveSearchClick,
  handleSaveSearchClick,
  handleSearchBusinessInputBlur
} from "./searchBusiness.helpers";
import searchBusinessesSignal from "./searchBusinesses.signal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { searchIcon } from "../Constant/Icons";
import { INDUSTRY_LIST, SEEKING_FUNDING_PAYMENT_RANGES } from "../Constant/constants";
import $appSettings from "signals/AppSettings.signal";
import BreadCrumbWrapper from "../BreadCrumbs/BreadCrumbs";
import { useEffect } from "react";
import currencyOptions from "../Constant/currencyOptions";
import SimpleTable from "../SimpleTable";
import SaveBusinessSearchModal from "../SearchBusinessSaveModal/SaveBusinessSearchModal";
import saveBusinessSearchSignal from "../SearchBusinessSaveModal/saveBusinessSearchModal.signal";
import {
  handleShowSaveSearchCreateModal,
  handleShowSaveSearchEditModal
} from "../SearchBusinessSaveModal/saveBusinessSearchModal.helpers";

const HEADERS = [
  {
    key: 'industry',
    title: 'Industry',
  },
  {
    key: 'zipCode',
    title: 'Zip Code',
  },
  {
    key: 'amountInterestedIn',
    title: 'Amount Interested In',
  },
  {
    key: 'action',
    title: 'Action',
  },
];

export const SearchBusinesses = () => {
  const { searchForm, searchFormErrors, savedSearches } =
    searchBusinessesSignal.value;

  const industrySectorOptions =
    $appSettings.value.constants?.INDUSTRY_SECTOR_OPTIONS ??
    ($appSettings.value.constants?.INDUSTRY_OPTIONS_GROUPED ?? []).map((g) => ({
      value: g.label,
      label: g.label,
    })) ??
    INDUSTRY_LIST.slice(0, 24).map((label) => ({ value: label, label }));

  useEffect(() => {
    fetchAndSetSavedSearches();

    return () => saveBusinessSearchSignal.reset();
  }, []);

  return (
    <BreadCrumbWrapper>
      <SaveBusinessSearchModal
        handleSaveSearchClick={handleSaveSearchClick}
        handleDeleteSaveSearchClick={handleDeleteSaveSearchClick}
      />
      <Card className="px-24 px-lg-48 pb-32 pt-16 rounded-0">
        <h3 className="text-center mb-16">Search Businesses</h3>
        <hr />
        <Form className="mb-32">
          <Row>
            <Col>
              <Form.Group className="form-group">
                <Field.Select
                  label="Industry"
                  labelClassName="font-weight-bold"
                  inputClassName="form-control"
                  name="industry"
                  value={searchForm.industry}
                  options={industrySectorOptions.filter((o) => o.value)}
                  onChange={({ name, value }) =>
                    handleBusinessSearchFormChange({ target: { name, value } })
                  }
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="form-group">
                <Form.Label className="font-weight-bold" htmlFor="location">
                  Zip Code
                </Form.Label>
                <Form.Control
                  type="text"
                  className="form-control"
                  id="location"
                  name="location"
                  aria-describedby="location"
                  value={searchForm.location}
                  isInvalid={searchFormErrors.location}
                  onChange={handleBusinessSearchFormChange}
                  onBlur={handleSearchBusinessInputBlur}
                />
                <Form.Control.Feedback type="invalid">
                  Please enter a valid Zip Code
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="form-group">
                <Field.Select
                  label="Amount Interested In"
                  labelClassName="font-weight-bold"
                  inputClassName="form-control"
                  name="fundingRange"
                  value={searchForm.fundingRange}
                  options={SEEKING_FUNDING_PAYMENT_RANGES.map((range) => ({
                    value: range,
                    label: range,
                  }))}
                  onChange={({ name, value }) =>
                    handleBusinessSearchFormChange({ target: { name, value } })
                  }
                />
              </Form.Group>
            </Col>
          </Row>
          <Row className="justify-content-end px-16">
            <Button
              className="rounded"
              onClick={() => handleBusinessSearchSubmit(searchForm)}
            >
              <FontAwesomeIcon icon={searchIcon} />
              <span className="ml-8">Search</span>
            </Button>
          </Row>
        </Form>
        <hr />
        <div className="mt-32">
          <h4>Saved Searches</h4>
          <div className="d-flex flex-row justify-content-end align-items-center mb-16">
            <Button
              className="rounded"
              onClick={handleShowSaveSearchCreateModal}
            >
              Save a Search
            </Button>
          </div>
          {!!savedSearches.length ? (
            <SimpleTable
              headers={HEADERS}
              rows={savedSearches.map((search) => {
                const { query } = search;
                const { where } = query || {};
                const industry = where?.assistance?.array_contains;
                const zipCode = where?.address?.equals;
                let amountInterestedIn = '';
                let fundingRange = '';

                if (where?.OR?.length) {
                  const { OR } = where;
                  const min = OR[0]?.assistance?.gte;
                  const max = OR[0]?.assistance?.lte;

                  if (min != null) {
                    const minFormatted = min.toLocaleString('en-US', currencyOptions.currencyUS);
                    const maxFormatted = (max ?? 10000000).toLocaleString('en-US', currencyOptions.currencyUS);
                    amountInterestedIn = `${minFormatted} - ${maxFormatted}`;
                    fundingRange = max != null ? `${min}-${max}` : `${min}`;
                  } else {
                    amountInterestedIn = '$100,001.00+';
                    fundingRange = '100001';
                  }
                }

                return {
                  industry: industry || '-',
                  zipCode: zipCode || '-',
                  amountInterestedIn: amountInterestedIn || '-',
                  action: ({ key }) => (
                    <td key={key} className="text-right">
                      <Button
                        size="sm"
                        className="rounded mr-8"
                        onClick={() =>
                          handleBusinessSearchSubmit({
                            industry,
                            location: zipCode,
                            fundingRange,
                          })
                        }
                      >
                        View Results
                      </Button>
                      <Button
                        variant="outline-dark"
                        size="sm"
                        className="rounded"
                        onClick={() =>
                          handleShowSaveSearchEditModal({
                            industry,
                            fundingRange: amountInterestedIn,
                            location: zipCode,
                            search,
                          })
                        }
                      >
                        Details
                      </Button>
                    </td>
                  ),
                };
              })}
            />
          ) : (
            <Alert>
              You do not have any saved searches. By saving a search, you will
              get search result notifications sent to you via email. Each time
              the query is run, you will receive notifications for new
              businesses (if any) that fit your search criteria.
            </Alert>
          )}
        </div>
      </Card>
    </BreadCrumbWrapper>
  );
};

export default SearchBusinesses;
